/* Yard Mint mobile menu: modal drawer with slide-in sub-levels.
   Native <dialog>.showModal() gives top-layer stacking, inert page and Android back/Esc (cancel).
   We add: open/close transitions, sub-panel navigation, a focus wrap, and Horizon's root scroll lock
   (html[scroll-lock] also locks the .page-wrapper scroll container). */
import { lockScroll, unlockScroll } from '@theme/utilities';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])';

class YmMobileMenu extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.toggle = this.querySelector('[data-ym-menu-open]');
    this.root = this.querySelector('[data-ym-panel="root"]');
    if (!this.dialog || !this.toggle) return;

    this.toggle.addEventListener('click', () => this.open());
    this.dialog.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('cancel', this.#onCancel);
    this.dialog.addEventListener('keydown', this.#onKeydown);
    this.mq = window.matchMedia('(min-width: 750px)');
    this.mq.addEventListener('change', this.#onBreakpoint);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.#onBreakpoint);
    window.removeEventListener('pageshow', this.#onPageShow);
    if (this.dialog) unlockScroll(this.dialog);
  }

  get isOpen() {
    return this.dialog.open;
  }

  open() {
    if (this.isOpen) return;
    this.#resetPanels();
    this.dialog.showModal();
    lockScroll(this.dialog);
    this.toggle.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ym-mm-open');
    requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
  }

  close({ instant = false } = {}) {
    if (!this.isOpen) return;
    this.dialog.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    const finish = () => {
      if (this.dialog.classList.contains('is-open')) return;
      this.dialog.close();
      unlockScroll(this.dialog);
      document.documentElement.classList.remove('ym-mm-open');
      this.#resetPanels();
      this.toggle.focus({ preventScroll: true });
    };
    if (instant || reduceMotion()) return finish();
    this.#afterTransition(this.dialog, finish, 420);
  }

  showPanel(id, opener) {
    const panel = this.dialog.querySelector(`#${CSS.escape(id)}`);
    if (!panel) return;
    this.opener = opener;
    opener?.setAttribute('aria-expanded', 'true');
    this.root.classList.remove('is-active');
    this.root.classList.add('is-behind');
    this.root.inert = true;
    panel.inert = false;
    panel.classList.add('is-active');
    panel.querySelector('.ym-mm__scroll')?.scrollTo(0, 0);
    const back = panel.querySelector('[data-ym-back]');
    if (reduceMotion()) back?.focus({ preventScroll: true });
    else this.#afterTransition(panel, () => back?.focus({ preventScroll: true }), 420);
  }

  back() {
    const panel = this.#activeSub();
    if (!panel) return;
    panel.classList.remove('is-active');
    panel.inert = true;
    this.root.inert = false;
    this.root.classList.remove('is-behind');
    this.root.classList.add('is-active');
    this.opener?.setAttribute('aria-expanded', 'false');
    this.opener?.focus({ preventScroll: true });
  }

  #activeSub() {
    return this.dialog.querySelector('[data-level="1"].is-active');
  }

  #resetPanels() {
    this.dialog.querySelectorAll('[data-level="1"]').forEach((p) => {
      p.classList.remove('is-active');
      p.inert = true;
    });
    this.dialog.querySelectorAll('[data-ym-sub]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    this.root.inert = false;
    this.root.classList.remove('is-behind');
    this.root.classList.add('is-active');
  }

  #afterTransition(el, cb, fallback) {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      cb();
    };
    const onEnd = (e) => {
      if (e.target === el && e.propertyName === 'transform') run();
    };
    el.addEventListener('transitionend', onEnd);
    setTimeout(run, fallback);
  }

  #onClick = (event) => {
    const t = event.target;
    // Backdrop click: the dialog element itself is the target outside its panels.
    if (t === this.dialog) {
      const r = this.dialog.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) return this.close();
    }
    if (!(t instanceof Element)) return;
    if (t.closest('[data-ym-close]')) return this.close();
    if (t.closest('[data-ym-back]')) return this.back();
    const sub = t.closest('[data-ym-sub]');
    if (sub) return this.showPanel(sub.getAttribute('data-ym-sub'), sub);
    const link = t.closest('a[href]');
    if (link && !event.defaultPrevented && !event.metaKey && !event.ctrlKey) {
      // Same-page anchors or the current page: just close.
      const url = new URL(link.href, location.href);
      if (url.pathname === location.pathname && url.search === location.search) this.close();
    }
  };

  #onCancel = (event) => {
    event.preventDefault();
    if (this.#activeSub()) this.back();
    else this.close();
  };

  #onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const scope = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[inert]') && el.getClientRects().length);
    if (!scope.length) return;
    const first = scope[0];
    const last = scope[scope.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #onBreakpoint = () => {
    if (this.mq.matches && getComputedStyle(this.toggle).display === 'none') this.close({ instant: true });
  };

  #onPageShow = (event) => {
    if (event.persisted && this.isOpen) this.close({ instant: true });
  };
}

if (!customElements.get('ym-mobile-menu')) customElements.define('ym-mobile-menu', YmMobileMenu);
